import React, { useCallback, useRef, useState } from "react";

import { AssetClassId } from "@fartherfinance/frontend/api/Investment/types";
import { Ticker } from "@fartherfinance/frontend/api/Types";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import {
  REASSIGN_SLEEVE_OR_SECURITY_IN_MODEL,
  REMOVE_ASSET_CLASS_OR_SLEEVE_OR_SECURITY_FROM_MODEL,
  SET_LOCKED,
  SET_WEIGHT,
} from "../../PortfolioBuilder/reducer/actions";
import { usePortfolioBuilderContext } from "../../PortfolioBuilder/reducer/Context";
import {
  AssetClassCategory,
  ElementID,
  ModelTableRowType,
} from "../../PortfolioBuilder/reducer/Types";
import ReAssignAssetClassModal from "../../ReAssignAssetClassModal/ReAssignAssetClassModal";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import ThreeDotsPopoverMenu from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/Menu";
import MenuItem from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/MenuItem";
import Text from "@src/sharedComponents/Typography/Text";
import { assertUnreachable } from "@src/utils/assertUnreachable";

import Lock from "./Lock/Lock";
import WeightInput from "./WeightInput/WeightInput";

import styles from "./TableRow.module.css";

const Space1 = 8; // mimics our --space-1 variable in our design system
export const Space3 = 3 * Space1; // mimics our --space-3 variable in our design system

interface Props {
  id: ElementID;
  ticker: Ticker;
  description: string;
  weight: string;
  isLocked: boolean;
  level: number; // nesting within the model/table
  assetClassCategory: AssetClassCategory;
  assetClassId: AssetClassId;
  isInSleeve: boolean;
  isInInvisibleAssetClass: boolean;
}

const SecurityRow: React.FC<Props> = ({
  id,
  ticker,
  description,
  weight,
  isLocked,
  level,
  assetClassCategory,
  assetClassId,
  isInSleeve,
  isInInvisibleAssetClass,
}) => {
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const onShowPopover = () => setShowPopover(true);
  const onHidePopover = () => setShowPopover(false);

  const [showReAssignModal, setShowReAssignModal] = useState<boolean>(false);
  const onShowReAssignModal = () => {
    setShowPopover(false);
    setShowReAssignModal(true);
  };
  const onHideReAssignModal = () => setShowReAssignModal(false);

  const anchorRef = useRef<HTMLDivElement | null>(null);

  const {
    color: {
      $chartSegmentOne,
      $chartSegmentTwo,
      $chartSegmentThree,
      $chartSegmentFour,
    },
  } = useTheme();

  const getColorForAssetClassCategory = useCallback(
    (assetClassCategory: AssetClassCategory) => {
      switch (assetClassCategory) {
        case "Equity":
          return $chartSegmentOne;
        case "Fixed Income":
          return $chartSegmentTwo;
        case "Cash Equivalents":
          return $chartSegmentThree;
        case "Other":
          return $chartSegmentFour;
        default:
          return assertUnreachable(assetClassCategory);
      }
    },
    [$chartSegmentOne, $chartSegmentTwo, $chartSegmentThree, $chartSegmentFour]
  );

  const { dispatch } = usePortfolioBuilderContext();

  const onChangeWeight = (newWeight: string) =>
    dispatch({
      type: SET_WEIGHT,
      payload: {
        id,
        weight: newWeight,
        assetClassId,
      },
    });

  const onClickLock = () =>
    dispatch({
      type: SET_LOCKED,
      payload: {
        id,
        isLocked: !isLocked,
      },
    });

  const onReAssign = (
    elementId: ElementID,
    fromAssetClassId: AssetClassId,
    toAssetClassElementId: ElementID
  ) =>
    dispatch({
      type: REASSIGN_SLEEVE_OR_SECURITY_IN_MODEL,
      payload: {
        elementId,
        fromAssetClassId,
        toAssetClassElementId,
        weight,
      },
    });

  const onRemoveSecurity = () =>
    dispatch({
      type: REMOVE_ASSET_CLASS_OR_SLEEVE_OR_SECURITY_FROM_MODEL,
      payload: {
        elementId: id,
        assetClassId,
        elementType: "Security" as Extract<ModelTableRowType, "Security">,
        weight,
      },
    });

  return (
    <>
      {showReAssignModal && (
        <ReAssignAssetClassModal
          isOpen={showReAssignModal}
          onClose={onHideReAssignModal}
          onSubmit={onReAssign}
          element={{
            id,
            type: "Security" as Extract<ModelTableRowType, "Security">,
            ticker,
            description,
          }}
          currentAssetClassId={assetClassId}
        />
      )}

      <div
        className={toClassName(styles.row, {
          [styles.securityOrSleeveInInvisibleAssetClass]:
            isInInvisibleAssetClass && level === 0,
        })}
      >
        <div className={styles.flexRow}>
          <div
            className={styles.takeUpSpace}
            style={{ width: `${Space1 + Space3 * level}px` }}
          />

          <div
            className={toClassName(styles.securityColorBox, {
              [styles.securityColorBoxBorder]: level === 0,
            })}
            style={{
              backgroundColor:
                level === 0
                  ? "inherit"
                  : getColorForAssetClassCategory(assetClassCategory),
            }}
          />

          <Text
            className={
              ticker.length > 4
                ? styles.securityTickerLabelLong
                : styles.securityTickerLabel
            }
            variant="body"
            color={"text"}
          >
            {ticker}
          </Text>

          <Text variant="body" color={"textSubtle"}>
            {description}
          </Text>
        </div>

        <div className={styles.flexRow}>
          {isInSleeve ? (
            <Text
              className={styles.disabledWeightText}
              variant="body"
              color={"textSubtle"}
            >
              {`${weight} %`}
            </Text>
          ) : (
            <WeightInput value={weight} onChange={onChangeWeight} />
          )}

          {false && <Lock isLocked={isLocked} onPress={onClickLock} />}

          {isInSleeve ? (
            <div className={styles.takeUpSpace} style={{ width: `${48}px` }} />
          ) : (
            <ThreeDotsPopoverMenu
              className={styles.threeDotsPopoverMenu}
              anchorRef={anchorRef}
              isOpen={showPopover}
              onOpen={onShowPopover}
              onClose={onHidePopover}
            >
              <MenuItem
                className={styles.popoverMenuItem}
                onClick={onShowReAssignModal}
              >
                Re-assign asset class
              </MenuItem>

              <MenuItem
                className={styles.popoverMenuItem}
                onClick={onRemoveSecurity}
              >
                Remove
              </MenuItem>
            </ThreeDotsPopoverMenu>
          )}
        </div>
      </div>
    </>
  );
};

export default SecurityRow;
